import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { useModalStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import useRequireAuth from '@/hooks/useRequireAuth';
import NavigationBarButtonContainer from '@/components/Navigation/NavigationBarButtonContainer';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';

// Ghost users are deprecated — the only logged-out state we care about
// is "no user at all". Each header action now funnels through the unified
// `useRequireAuth` so anon visitors get the standard `AuthCTAModal`
// instead of the old `GhostSignupModal`.
const FilesRouteNavigationHeaderRight = React.memo(() => {
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const { openModal } = useModalStore(state => state.actions);
  const requireAuth = useRequireAuth();

  const handleSearchClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    requireAuth(
      () => router.push('/files/search'),
      {
        source: 'files_header_search',
        title: 'Sign in to search your files',
        subtitle: 'File search runs against your personal library, so you\u2019ll need an account before we can find anything.',
        icon: 'folder-magnifying-glass',
      }
    );
  }, [router, requireAuth]);

  const handleUpload = useCallback(() => {
    requireAuth(
      () => openModal('FileUploadModal', { libraryUpload: true }),
      {
        source: 'files_header_upload',
        title: 'Sign in to upload files',
        subtitle: 'File uploads are saved to your account so chats can reference them later.',
        icon: 'cloud-arrow-up',
      }
    );
  }, [openModal, requireAuth]);

  const handleNewCollection = useCallback(() => {
    requireAuth(
      () => openModal('CreateCollectionModal'),
      {
        source: 'files_header_new_collection',
        title: 'Sign in to create a Collection',
        subtitle: 'Collections group files and chats so Gab can pull from the same context across every conversation.',
        icon: 'folder-plus',
      }
    );
  }, [openModal, requireAuth]);

  // Only show nav buttons on mobile (desktop has them in page header)
  if (!isXS) {
    return null;
  }

  return (
    <NavigationBarButtonContainer rightPadding>
      <NavigationBarButton
        icon="cloud-arrow-up"
        onPress={handleUpload}
        accessibilityLabel={t('nav.routes.files.upload')}
      />
      <NavigationBarButton
        icon="folder-plus"
        onPress={handleNewCollection}
        accessibilityLabel={t('nav.routes.files.newCollection')}
      />
      <NavigationBarButton
        icon="magnifying-glass"
        onPress={handleSearchClick}
        accessibilityLabel={t('nav.routes.files.searchFiles')}
      />
    </NavigationBarButtonContainer>
  );
});

FilesRouteNavigationHeaderRight.displayName = 'FilesRouteNavigationHeaderRight';

// Function to get navigation options - hides header on desktop
const getFilesRouteNavigationOptions = (isXS) => ({
  title: 'Files',
  headerTitle: isXS ? 'Files' : '',
  headerShown: isXS, // Hide entire header on desktop
  headerBackVisible: false,
  headerRight: () => <FilesRouteNavigationHeaderRight />,
});

// Default export for backwards compatibility (mobile gets title)
const FilesRouteNavigationOptions = {
  title: 'Files',
  headerTitle: 'Files',
  headerBackVisible: false,
  headerRight: () => <FilesRouteNavigationHeaderRight />,
};

export { getFilesRouteNavigationOptions };
export default FilesRouteNavigationOptions; 